import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import createInvoiceItemColumns from "../Utils/InvoiceItemColumns";
import InvoiceFormLayout from "./InvoiceFormLayout";
import {
  getAllPartners,
  getAllProducts,
  getAllCategories,
  getAccountTree,
  getInvoiceById,
  createInvoiceWithFile,
  updateInvoiceWithFile,
  getMailingOffers,
  splitInvoice,
  clearAccountsCache,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import { useSelector } from "react-redux";
import { getLists, getSegments } from "../../../../../core/api/mediaplan";
import {
  getOffersByBrand,
  getMarketingFilesByBrand,
} from "../../../../../core/api/marketing";
import dayjs from "dayjs";
import {
  calculateTotals,
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  formatInvoiceData,
  prepareFormData,
} from "../../../utils/invoiceUtils";
import { convertFromUSD, convertToUSD } from "../../../utils/currencyUtils";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";

const CreateInvoiceModal = ({
  visible,
  onCancel,
  onSuccess,
  selectedBrand,
  invoiceId = null,
  defaultInvoiceType = "invoice",
  // New props for pre-filling from PaymentAllocation
  initialPartnerId = null,
  initialDescription = null,
  initialAmount = null,
  initialIssueDate = null, // Transaction date to use as issue date
  initialInvoiceNumber = null, // Transaction ID to use as invoice/bill number
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const { decimalPrecision } = useDisplayCurrency();
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [mailingOffers, setMailingOffers] = useState([]);
  const [marketingFiles, setMarketingFiles] = useState([]);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(selectedBrand);
  const [customRates, setCustomRates] = useState({});
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_taxAmount: 0,
    calculated_discountAmount: 0,
    calculated_total: 0,
    match: true,
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // Ref to track if initial data has been pre-filled (prevents re-setting values on re-renders)
  const initialDataPrefilledRef = React.useRef(false);

  // State for multiple payment terms - START WITH 1 DEFAULT TERM
  const [paymentTerms, setPaymentTerms] = useState([
    {
      id: Date.now(),
      code: "",
      description: "",
      percentage: 100,
      due_days: 30,
      amount: 0,
      active: true,
    },
  ]);
  const INVOICE_DRAFT_KEY = "invoiceDraft";

  useEffect(() => {
    if (visible) {
      try {
        const savedRates = localStorage.getItem("custom_currency_rates");
        if (savedRates) {
          setCustomRates(JSON.parse(savedRates));
        }
      } catch (e) {
        console.error("Failed to load custom rates from localStorage", e);
      }
    }
  }, [visible]);

  // Use useRef to hold the debounce timer for stable reference across renders
  const draftSaveTimerRef = React.useRef(null);
  const partnerFetchSeqRef = React.useRef(0);

  useEffect(() => {
    if (!isEditMode && selectedBrand) {
      // Clear any pending save
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }

      // Debounced save - wait 500ms before saving
      draftSaveTimerRef.current = setTimeout(() => {
        const values = form.getFieldsValue();
        const draft = {
          formValues: values,
          invoiceItems,
          paymentTerms,
        };
        sessionStorage.setItem(INVOICE_DRAFT_KEY, JSON.stringify(draft));
      }, 500);
    }

    // Cleanup on unmount
    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [form, invoiceItems, paymentTerms, isEditMode, selectedBrand]);

  useEffect(() => {
    if (!isEditMode && selectedBrand) {
      const draft = sessionStorage.getItem(INVOICE_DRAFT_KEY);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          if (parsed.formValues) {
            if (parsed.formValues.issueDate) {
              parsed.formValues.issueDate = dayjs(parsed.formValues.issueDate);
            }

            form.setFieldsValue(parsed.formValues);
          }
          if (parsed.invoiceItems) {
            setInvoiceItems(parsed.invoiceItems);
            // Recalculate totals after restoring items - will be handled by the useEffect below
          }
          if (parsed.paymentTerms) setPaymentTerms(parsed.paymentTerms);
        } catch (e) {
          console.warn("❌ Failed to parse invoice draft:", e);
        }
      }
    }
  }, [isEditMode, selectedBrand, form]);

  // Data fetching functions
  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetchBrandsBySelectedClients(selectedClients);
      const brandsArray = response.data || [];
      setBrands(brandsArray);
      setFilteredBrands(brandsArray); // Initialize filtered brands with all brands
    } catch (error) {
      console.error("Error fetching brands:", error);
      message.error("Failed to fetch brands");
    }
  }, [selectedClients]);

  const fetchPartners = useCallback(
    async (invoiceTypeOverride = null, brandOverride = null) => {
      const seq = ++partnerFetchSeqRef.current;
      try {
        const brandToUse = brandOverride || currentBrand;
        const filters = brandToUse
          ? { brand: brandToUse.id || brandToUse }
          : {};
        const response = await getAllPartners(filters);
        if (seq !== partnerFetchSeqRef.current) return [];
        const invoiceType = invoiceTypeOverride || defaultInvoiceType;
        let filteredPartners = response.data || [];
        if (invoiceType === "bill") {
          filteredPartners = filteredPartners.filter(
            (partner) =>
              partner.partner_type === "vendor" ||
              partner.partner_type === "both"
          );
        } else if (invoiceType === "invoice") {
          filteredPartners = filteredPartners.filter(
            (partner) =>
              partner.partner_type === "customer" ||
              partner.partner_type === "both"
          );
        }
        setPartners(filteredPartners);
        return filteredPartners;
      } catch (error) {
        if (seq !== partnerFetchSeqRef.current) return [];
        console.error("Error fetching partners:", error.response || error);
        message.error("Failed to fetch partners");
        return [];
      }
    },
    [currentBrand, defaultInvoiceType]
  );

  const fetchCategories = useCallback(async () => {
    try {
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      const response = await getAllCategories(filters);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error.response || error);
      message.error("Failed to fetch categories");
    }
  }, [currentBrand]);

  const fetchAccounts = useCallback(
    async (brandOverride = null) => {
      try {
        // Accounts are GLOBAL - they don't have a brand field
        // The backend ignores brand parameter, so we can fetch accounts without brand
        // But we still need a brand selected for the invoice itself
        const brandToUse = brandOverride || currentBrand || selectedBrand;

        // Fetch accounts without brand filter (accounts are global)
        const response = await getAccountTree({});

        // getAccountTree returns response.data directly (see api/accounting.js line 2104)
        // Handle different response formats for safety
        let accountsData = [];
        if (Array.isArray(response)) {
          accountsData = response;
        } else if (response && Array.isArray(response.data)) {
          accountsData = response.data;
        } else if (response && Array.isArray(response.results)) {
          accountsData = response.results;
        } else if (response && typeof response === "object") {
          // If response is an object but not an array, try to extract data
          accountsData = response.data || response.results || [];
        }

        if (accountsData && accountsData.length > 0) {
          setAccounts(accountsData);
        } else {
          setAccounts([]);
        }
      } catch (error) {
        console.error(
          "❌ Error fetching account tree:",
          error.response || error
        );
        console.error(
          "❌ Error details:",
          error.response?.data || error.message
        );
        console.error("❌ Full error:", error);
        message.error(
          `Failed to fetch chart of accounts: ${
            error.response?.data?.detail ||
            error.response?.data?.error ||
            error.message ||
            "Unknown error"
          }`
        );
        setAccounts([]);
      }
    },
    [currentBrand, selectedBrand]
  );

  const fetchProducts = useCallback(
    async (vendorId = null) => {
      try {
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllProducts(filters);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error.response || error);
        message.error("Failed to fetch products");
      }
    },
    [currentBrand]
  );

  const fetchOffers = useCallback(async () => {
    try {
      if (!currentBrand) {
        setOffers([]);
        return;
      }
      const res = await getOffersByBrand(currentBrand.id);
      const list = Array.isArray(res?.data) ? res.data : res;
      setOffers(list || []);
    } catch (error) {
      console.error("Error fetching offers:", error.response || error);
      // Don't show error message - offers are optional
      setOffers([]);
    }
  }, [currentBrand]);

  const fetchMailingOffers = useCallback(async () => {
    try {
      if (!currentBrand) {
        setMailingOffers([]);
        return;
      }
      const filters = { brand: currentBrand.id };
      const response = await getMailingOffers(filters);
      setMailingOffers(response.data || []);
    } catch (error) {
      console.error("Error fetching mailing offers:", error.response || error);
      // Don't show error message - mailing offers are optional
      setMailingOffers([]);
    }
  }, [currentBrand]);

  const fetchLists = useCallback(async () => {
    try {
      const response = await getLists();
      setLists(response.data || []);
      setSegments([]);
    } catch (error) {
      console.error("Error fetching lists:", error.response || error);
      message.error("Failed to fetch lists");
    }
  }, []);

  const fetchSegments = useCallback(async (listId) => {
    try {
      const response = await getSegments({ list: listId });
      setSegments(response.data || []);
    } catch (error) {
      console.error("Error fetching segments:", error.response || error);
      message.error("Failed to fetch segments");
    }
  }, []);

  const fetchMarketingFiles = useCallback(async () => {
    try {
      if (!currentBrand) {
        setMarketingFiles([]);
        return;
      }
      const res = await getMarketingFilesByBrand(currentBrand.id);
      const list = Array.isArray(res?.data) ? res.data : res;
      setMarketingFiles(list || []);
    } catch (error) {
      console.error("Error fetching marketing files:", error.response || error);
      // Don't show error message - marketing files are optional
      setMarketingFiles([]);
    }
  }, [currentBrand]);

  const handleBrandChange = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    setCurrentBrand(brand);
    form.setFieldsValue({ brand: brandId });
    // Clear dependent data when brand changes
    setPartners([]);
    setProducts([]);
    setCategories([]);
    setAccounts([]);
    setOffers([]);
    setMailingOffers([]);
    setMarketingFiles([]);
    // Fetch partners, categories, and accounts immediately when brand changes
    if (brand) {
      const filters = { brand: brand.id };
      // Fetch partners
      getAllPartners(filters)
        .then((response) => {
          // Filter partners based on invoice type
          let filteredPartners = response.data;
          if (defaultInvoiceType === "bill") {
            // For bills, only show vendors
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "vendor" ||
                partner.partner_type === "both"
            );
          } else if (defaultInvoiceType === "invoice") {
            // For invoices, only show customers
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "customer" ||
                partner.partner_type === "both"
            );
          }
          setPartners(filteredPartners);
        })
        .catch((error) => {
          console.error("Error fetching partners:", error.response || error);
          message.error("Failed to fetch partners");
        });

      // CRITICAL: Fetch categories immediately when brand changes
      // This ensures categories dropdown is populated right away
      fetchCategories().catch(() => {
        // Error already handled in fetchCategories
      });

      // CRITICAL: Fetch accounts immediately when brand changes
      // This ensures chart of accounts dropdown is populated right away
      // Note: Accounts are global, but we still fetch them when brand changes
      fetchAccounts().catch(() => {
        // Error already handled in fetchAccounts
      });
    }
  };

  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const handlePartnerChange = (value) => {
    const selectedPartner = partners.find((p) => p.id === value);
    setSelectedPartner(selectedPartner);

    if (selectedPartner) {
      // Filter brands by partner
      if (selectedPartner.brands && selectedPartner.brands.length > 0) {
        const partnerBrandIds = selectedPartner.brands;
        const filtered = brands.filter((b) => partnerBrandIds.includes(b.id));
        setFilteredBrands(filtered);

        // If only one brand, auto-select it
        if (filtered.length === 1) {
          setCurrentBrand(filtered[0]);
          form.setFieldsValue({ brand: filtered[0].id });
        } else {
          // Keep current brand if it's in the filtered list, otherwise don't clear
          const currentBrandId =
            currentBrand?.id || form.getFieldValue("brand");
          if (currentBrandId && filtered.some((b) => b.id === currentBrandId)) {
            // Current brand is valid for this partner, keep it
          } else if (filtered.length > 0) {
            // Current brand not in partner's brands, but don't auto-clear
            // Let user choose from filtered list
          }
        }
      } else {
        // Partner has no specific brands, show all brands - keep current selection
        setFilteredBrands(brands);
      }

      if (
        selectedPartner.partner_type === "vendor" ||
        selectedPartner.partner_type === "both"
      ) {
        fetchProducts(selectedPartner.id);
        fetchCategories(selectedPartner.id);
      } else {
        fetchProducts();
        fetchCategories();
      }
    }
  };

  // Payment terms management - EXACT SAME LOGIC AS ORIGINAL
  const addPaymentTerm = () => {
    const newTermsCount = paymentTerms.length + 1;
    const evenPercentage = Math.floor(100 / newTermsCount);
    const remainder = 100 % newTermsCount;

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    // Rebuild all terms with updated percentage
    const updatedTerms = paymentTerms.map((term, index) => {
      const percent =
        evenPercentage + (index === newTermsCount - 1 ? remainder : 0);
      return {
        ...term,
        percentage: percent,
        amount: (percent / 100) * calculatedTotal,
        due_days: 30 * (index + 1),
      };
    });

    // Add the new term
    const newPercentage =
      evenPercentage +
      (paymentTerms.length === newTermsCount - 1 ? remainder : 0);
    updatedTerms.push({
      id: Date.now(),
      code: "",
      description: "",
      percentage: newPercentage,
      due_days: 30 * newTermsCount,
      amount: (newPercentage / 100) * calculatedTotal,
      active: true,
    });

    setPaymentTerms(updatedTerms);
  };

  const removePaymentTerm = (id) => {
    const remainingTerms = paymentTerms.filter((term) => term.id !== id);

    // If no terms left, don't redistribute
    if (remainingTerms.length === 0) {
      setPaymentTerms([]);
      return;
    }

    // Redistribute percentages evenly among remaining terms
    const basePercentage = Math.floor(100 / remainingTerms.length);
    const remainder = 100 - basePercentage * remainingTerms.length;

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    const redistributedTerms = remainingTerms.map((term, index) => {
      // Give the remainder to the last term to ensure total is exactly 100%
      const percentage =
        basePercentage + (index === remainingTerms.length - 1 ? remainder : 0);
      return {
        ...term,
        percentage,
        amount: (percentage / 100) * calculatedTotal,
      };
    });

    setPaymentTerms(redistributedTerms);
  };

  const updatePaymentTerm = (id, field, value) => {
    if (field === "percentage") {
      const otherTermsTotal = paymentTerms.reduce((sum, term) => {
        return term.id !== id ? sum + parseFloat(term.percentage || 0) : sum;
      }, 0);

      if (otherTermsTotal + parseFloat(value || 0) > 100) {
        const maxAllowed = 100 - otherTermsTotal;
        message.warning(
          `Total percentage cannot exceed 100%. Setting to maximum allowed: ${maxAllowed.toFixed(
            2
          )}%`
        );
        value = maxAllowed;
      }
    }

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    const updatedTerms = paymentTerms.map((term) => {
      if (term.id === id) {
        const updatedTerm = { ...term, [field]: value };

        if (field === "percentage") {
          updatedTerm.amount = (value / 100) * calculatedTotal;
        }

        return updatedTerm;
      }
      return term;
    });
    setPaymentTerms(updatedTerms);
  };

  // Fetch invoice data for editing
  const fetchInvoiceData = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const invoiceData = await getInvoiceById(id);

        const currency = invoiceData.currency || "$";
        const isUSD = currency === "$" || currency === "USD";

        // Convert amounts from USD to original currency for display if not USD
        const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
        const displayInvoiceData = isUSD
          ? invoiceData
          : {
              ...invoiceData,
              subtotal: round3(
                convertFromUSD(invoiceData.subtotal, currency, customRates)
              ),
              discount_amount: round3(
                convertFromUSD(
                  invoiceData.discount_amount,
                  currency,
                  customRates
                )
              ),
              total_amount: round3(
                convertFromUSD(invoiceData.total_amount, currency, customRates)
              ),
              items: invoiceData.items.map((item) => {
                const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
                return {
                  ...item,
                  unit_price: round3(
                    convertFromUSD(item.unit_price, currency, customRates)
                  ),
                  subtotal: round3(
                    convertFromUSD(item.subtotal, currency, customRates)
                  ),
                  tax_amount: round3(
                    convertFromUSD(item.tax_amount, currency, customRates)
                  ),
                  discount_amount: round3(
                    convertFromUSD(item.discount_amount, currency, customRates)
                  ),
                };
              }),
              payment_terms: (invoiceData.payment_terms || []).map((term) => ({
                ...term,
                amount: round3(
                  convertFromUSD(term.amount, currency, customRates)
                ),
              })),
            };

        // Set the brand from the invoice data if editing
        if (displayInvoiceData.brand) {
          // If brand is just an ID, find the full brand object from brands array
          let fullBrand = displayInvoiceData.brand;
          if (
            typeof displayInvoiceData.brand === "number" ||
            typeof displayInvoiceData.brand === "string"
          ) {
            const foundBrand = brands.find(
              (b) => b.id === Number(displayInvoiceData.brand)
            );
            if (foundBrand) {
              fullBrand = foundBrand;
            }
          } else if (displayInvoiceData.brand.id) {
            const foundBrand = brands.find(
              (b) => b.id === displayInvoiceData.brand.id
            );
            fullBrand = foundBrand || displayInvoiceData.brand;
          }

          setCurrentBrand(fullBrand);

          // Show all available brands so the user can change the brand when editing
          setFilteredBrands(brands);

          // Fetch accounts and partners immediately after setting brand for edit mode
          if (fullBrand) {
            // Accounts are global, fetch them without brand filter
            await fetchAccounts();
            // Fetch partners using the invoice's invoice_type and brand for correct filtering
            await fetchPartners(
              displayInvoiceData.invoice_type || defaultInvoiceType,
              fullBrand
            );

            // Small delay to ensure state is updated
            await new Promise((resolve) => setTimeout(resolve, 100));
          } else {
            // If no brand in invoice data, still fetch accounts (they're global)
            await fetchAccounts();
          }
        } else {
          // If no brand in invoice data, still fetch accounts (they're global)
          await fetchAccounts();
        }

        // Extract partner ID for form (partner might be an object or ID)
        let partnerId =
          displayInvoiceData.partner?.id || displayInvoiceData.partner || null;
        // Convert to number if it's a string
        if (partnerId && typeof partnerId === "string") {
          partnerId = parseInt(partnerId, 10);
        }
        // Convert to number if it's not already
        if (partnerId && typeof partnerId !== "number" && !isNaN(partnerId)) {
          partnerId = Number(partnerId);
        }

        // Set the partner for filtering brands
        if (displayInvoiceData.partner) {
          const partnerObj =
            typeof displayInvoiceData.partner === "object"
              ? displayInvoiceData.partner
              : null;
          setSelectedPartner(partnerObj || partnerId);
        }

        const formattedItems = displayInvoiceData.items.map((item) => {
          const offerDetails = item.offer_details || {};
          const variations = Array.isArray(
            offerDetails.product_variations_detail
          )
            ? offerDetails.product_variations_detail
            : [];
          const firstVariation = variations.length > 0 ? variations[0] : null;

          const derivedProductName =
            item.product_name ||
            firstVariation?.name ||
            offerDetails.name ||
            "";

          const derivedProductVariation =
            (item.product_variation != null && item.product_variation !== "")
              ? item.product_variation
              : (firstVariation?.description || offerDetails.product_variation || offerDetails.variation || "");

          const derivedProductId =
            item.product || firstVariation?.product_id || null;
          const normalizedOfferId =
            item.offer || item.offer_id || offerDetails?.id || null;

          const productType = item.product_type || "general";
          const printType = item.print_type || null;
          const envelope = item.envelope || null;
          const marketingOffer = item.marketing_offer || null;

          return {
            key: item.id,
            productId: derivedProductId,
            categoryId: item.category,
            accountId:
              item.account?.id ||
              (typeof item.account === "number" ? item.account : null) ||
              item.account_id ||
              null,
            productName: derivedProductName,
            productVariation: derivedProductVariation,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            taxRate: item.tax_rate,
            taxAmount: item.tax_amount,
            discountAmount: item.discount_amount || 0,
            subtotal: item.subtotal,
            total_amount: item.total,
            offerId: normalizedOfferId,
            offerIds: normalizedOfferId ? [normalizedOfferId] : [],
            productType,
            printType,
            envelope,
            marketingOffer,
            listId: item.list || null,
            segmentId: item.segment || null,
            // Payment terms per line item (array of payment terms)
            payment_terms: item.payment_terms || [],
          };
        });

        const formValues = {
          partner: partnerId, // Use partner ID, not the full object
          brand: displayInvoiceData.brand?.id || displayInvoiceData.brand,
          issueDate: dayjs(displayInvoiceData.issue_date),
          invoiceType: displayInvoiceData.invoice_type,
          number: displayInvoiceData.number,
          currency: displayInvoiceData.currency,
          status: displayInvoiceData.status,
          notes: displayInvoiceData.notes,
          subtotal: displayInvoiceData.subtotal || 0,
          discount_amount: displayInvoiceData.discount_amount || 0,
          tax_rate: displayInvoiceData.tax_rate || 0,
          total_amount: displayInvoiceData.total_amount || 0,
          fileUrl: displayInvoiceData.file || null,
        };

        form.setFieldsValue(formValues);
        setInvoiceItems(formattedItems);

        if (formattedItems.length > 0) {
          const listIds = [
            ...new Set(
              formattedItems.map((item) => item.listId).filter((id) => id)
            ),
          ];
          if (listIds.length > 0) {
            Promise.all(listIds.map((id) => getSegments({ list: id })))
              .then((results) => {
                const allSegments = results.flatMap((res) => res.data || []);
                setSegments(allSegments);
              })
              .catch((error) => {
                console.error(
                  "Error fetching segments for invoice lists:",
                  error
                );
                message.error("Failed to pre-load segment data.");
              });
          }
        }

        const calculatedValues = calculateTotals(formattedItems, form);
        setCalculatedValues(calculatedValues);

        // Load invoice-level payment terms
        if (
          displayInvoiceData.payment_terms &&
          Array.isArray(displayInvoiceData.payment_terms) &&
          displayInvoiceData.payment_terms.length > 0
        ) {
          // Use calculated total instead of form total_amount
          const calculatedTotal = calculatedValues.calculated_total || 0;

          const updatedPaymentTerms = displayInvoiceData.payment_terms.map(
            (term) => {
              const amount = (term.percentage / 100) * calculatedTotal;
              return {
                id: term.id || Date.now(),
                code: term.code || "",
                description: term.description || "",
                percentage: term.percentage || 100,
                due_days: term.due_days ?? 30, // Use ?? to allow 0 as valid value
                amount: amount,
                active: term.active !== undefined ? term.active : true,
              };
            }
          );

          setPaymentTerms(updatedPaymentTerms);
        } else {
          // If no invoice-level payment terms, check if items have payment terms
          // If all items have the same payment terms, use those as invoice-level
          const itemPaymentTerms = formattedItems
            .map((item) => item.payment_terms || [])
            .filter((terms) => terms.length > 0);

          if (itemPaymentTerms.length > 0) {
            // Use the first item's payment terms as invoice-level (they should all be the same)
            const firstItemTerms = itemPaymentTerms[0];
            const calculatedTotal = calculatedValues.calculated_total || 0;

            const updatedPaymentTerms = firstItemTerms.map((term) => {
              const amount = (term.percentage / 100) * calculatedTotal;
              return {
                id: term.id || Date.now(),
                code: term.code || "",
                description: term.description || "",
                percentage: term.percentage || 100,
                due_days: term.due_days ?? 30, // Use ?? to allow 0 as valid value
                amount: amount,
                active: term.active !== undefined ? term.active : true,
              };
            });

            setPaymentTerms(updatedPaymentTerms);
          }
        }
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        message.error("Failed to fetch invoice data");
      } finally {
        setLoading(false);
      }
    },
    [
      form,
      setInvoiceItems,
      setCalculatedValues,
      setLoading,
      setPaymentTerms,
      brands,
      fetchAccounts,
      fetchPartners,
      defaultInvoiceType,
      customRates,
      decimalPrecision,
    ]
  );

  // Invoice items management
  const addItem = () => {
    const newItems = [
      ...invoiceItems,
      {
        key: Date.now(),
        productId: null,
        categoryId: null,
        accountId: null,
        productName: "",
        productVariation: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        taxAmount: 0,
        discountAmount: 0,
        subtotal: 0,
        total: 0,
        offerId: null,
        offerIds: [],
        productType: "general",
        printType: null,
        envelope: null,
        marketingOffer: null,
        listId: null,
        segmentId: null,
        // Payment terms per line item (array of payment terms)
        payment_terms: [],
      },
    ];
    setInvoiceItems(newItems);
    // Recalculate totals after adding item
    setTimeout(() => {
      calculateInvoiceTotals(newItems);
    }, 0);
  };

  const removeItem = (key) => {
    const updatedItems = invoiceItems.filter((item) => item.key !== key);
    setInvoiceItems(updatedItems);
    calculateInvoiceTotals(updatedItems);
  };

  const handleProductChange = (value, key) => {
    const updatedItems = handleProductChangeUtil(
      value,
      key,
      products,
      invoiceItems
    );
    if (updatedItems !== invoiceItems) {
      setInvoiceItems(updatedItems);
      calculateInvoiceTotals(updatedItems);
    }
  };

  const handleItemChange = (key, field, value) => {
    const updatedItems = handleItemChangeUtil(key, field, value, invoiceItems);
    setInvoiceItems(updatedItems);
    calculateInvoiceTotals(updatedItems);
  };

  const clearDraft = () => {
    sessionStorage.removeItem(INVOICE_DRAFT_KEY);
  };

  const handleInvoiceError = (error, isEditMode) => {
    const action = isEditMode ? "update" : "create";

    // Log full error for debugging
    console.error(`Error ${action}ing invoice:`, error);
    console.error("Error response:", error.response?.data);

    if (error.response?.data?.detail) {
      message.error(
        `Failed to ${action} invoice: ${error.response.data.detail}`
      );
      return;
    }

    const errs = error.response?.data;
    if (errs && typeof errs === "object") {
      // Check for items array errors (nested validation errors)
      if (errs.items && Array.isArray(errs.items)) {
        errs.items.forEach((itemError, index) => {
          if (typeof itemError === "object" && itemError !== null) {
            Object.entries(itemError).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach((msg) => {
                  message.error(`Item ${index + 1} - ${field}: ${msg}`);
                });
              } else {
                message.error(`Item ${index + 1} - ${field}: ${messages}`);
              }
            });
          } else {
            message.error(`Item ${index + 1}: ${itemError}`);
          }
        });
        return;
      }

      // Handle other field errors
      Object.entries(errs).forEach(([field, messages]) => {
        // Skip non-field errors
        if (field === "error" || field === "message") {
          message.error(`${field}: ${messages}`);
          return;
        }

        // Handle array of errors
        if (Array.isArray(messages)) {
          messages.forEach((msg) => {
            // If msg is an object (nested error), stringify it
            if (typeof msg === "object" && msg !== null) {
              message.error(`${field}: ${JSON.stringify(msg)}`);
            } else {
              message.error(`${field}: ${msg}`);
            }
          });
        } else if (typeof messages === "object" && messages !== null) {
          // Handle nested object errors
          message.error(`${field}: ${JSON.stringify(messages)}`);
        } else {
          message.error(`${field}: ${messages}`);
        }
      });
      return;
    }

    message.error(
      `Failed to ${action} invoice: ${error.message || "Unknown error"}`
    );
  };

  const calculateInvoiceTotals = useCallback(
    (items, formInstance) => {
      const calculatedValues = calculateTotals(items, formInstance || form);
      setCalculatedValues(calculatedValues);

      // Update form fields immediately with calculated values
      if (formInstance || form) {
        const formToUpdate = formInstance || form;
        formToUpdate.setFieldsValue({
          subtotal: calculatedValues.calculated_subtotal,
          total_amount: calculatedValues.calculated_total,
        });
      }

      // Use the calculated total instead of form total_amount
      const calculatedTotal = calculatedValues.calculated_total || 0;

      if (paymentTerms.length > 0) {
        const updatedTerms = paymentTerms.map((term) => {
          return {
            ...term,
            amount: (term.percentage / 100) * calculatedTotal,
          };
        });
        setPaymentTerms(updatedTerms);
      }

      return calculatedValues;
    },
    [form, paymentTerms, setCalculatedValues, setPaymentTerms]
  );

  // Recalculate totals whenever invoiceItems change (must be after calculateInvoiceTotals is defined)
  useEffect(() => {
    if (invoiceItems.length > 0) {
      calculateInvoiceTotals(invoiceItems);
    }
  }, [invoiceItems.length, calculateInvoiceTotals]); // Include calculateInvoiceTotals in deps

  const onFinish = async (values) => {
    if (!currentBrand) {
      message.error("Please select a brand before saving the invoice");
      return;
    }

    // Validate that all items have descriptions
    const itemsWithoutDescription = invoiceItems.filter(
      (item) => !item.description || item.description.trim() === ""
    );
    if (itemsWithoutDescription.length > 0) {
      message.error(
        `Please add a description for all line items. ${itemsWithoutDescription.length} item(s) missing description.`
      );
      return;
    }

    if (paymentTerms.length === 0) {
      message.error("Please add at least one payment term");
      return;
    }

    const totalPercentage = paymentTerms.reduce(
      (sum, term) => sum + parseFloat(term.percentage || 0),
      0
    );
    if (Math.abs(totalPercentage - 100) > 0.01) {
      message.error(
        `Payment term percentages must add up to 100%. Current total: ${totalPercentage}%`
      );
      return;
    }

    // Check for duplicate payment terms (same percentage AND due_days)
    const termKeys = paymentTerms.map(
      (term) =>
        `${parseFloat(term.percentage || 0).toFixed(2)}-${term.due_days}`
    );
    const uniqueKeys = new Set(termKeys);
    if (uniqueKeys.size !== termKeys.length) {
      message.error(
        "Duplicate payment terms found. Each combination of percentage and due days must be unique."
      );
      return;
    }

    await submitInvoice(values);
  };

  const submitInvoice = async (values) => {
    setLoading(true);
    try {
      // Ensure the invoice type is set correctly
      const valuesWithType = {
        ...values,
        invoiceType: defaultInvoiceType,
      };

      const invoiceData = formatInvoiceData(
        valuesWithType,
        calculatedValues,
        invoiceItems,
        currentBrand
      );

      // CRITICAL: Convert ALL amounts to USD if currency is not USD
      // Everything in the database should be stored in USD for consistency
      const currency = values.currency || "$";
      const isUSD = currency === "$" || currency === "USD";

      let finalInvoiceData = { ...invoiceData };
      let paymentTermsToSend = paymentTerms;

      const round3 = (val) => Number(Number(val || 0).toFixed(3));

      if (!isUSD) {
        // Convert invoice-level amounts to USD and round to 3 decimals
        finalInvoiceData.subtotal = round3(
          convertToUSD(invoiceData.subtotal || 0, currency, customRates)
        );
        finalInvoiceData.discount_amount = round3(
          convertToUSD(invoiceData.discount_amount || 0, currency, customRates)
        );
        finalInvoiceData.total_amount = round3(
          convertToUSD(invoiceData.total_amount || 0, currency, customRates)
        );

        // Convert item-level amounts to USD and round to 3 decimals
        finalInvoiceData.items = invoiceData.items.map((item) => ({
          ...item,
          unit_price: round3(
            convertToUSD(item.unit_price || 0, currency, customRates)
          ),
          subtotal: round3(
            convertToUSD(item.subtotal || 0, currency, customRates)
          ),
          tax_amount: round3(
            convertToUSD(item.tax_amount || 0, currency, customRates)
          ),
          discount_amount: round3(
            convertToUSD(item.discount_amount || 0, currency, customRates)
          ),
          // Convert item payment terms amounts to USD
          payment_terms: (item.payment_terms || []).map((term) => ({
            ...term,
            amount: round3(
              convertToUSD(term.amount || 0, currency, customRates)
            ),
          })),
        }));

        // Convert invoice-level payment terms amounts to USD
        paymentTermsToSend = paymentTerms.map((term) => ({
          ...term,
          amount: round3(convertToUSD(term.amount || 0, currency, customRates)),
        }));
      }

      const formData = prepareFormData(finalInvoiceData, values);

      formData.append("payment_terms", JSON.stringify(paymentTermsToSend));

      if (isEditMode) {
        await updateInvoiceWithFile(invoiceId, formData);
        message.success("Invoice updated successfully");
        clearDraft();
      } else {
        await createInvoiceWithFile(formData, "v2");
        message.success("Invoice created successfully");
        clearDraft();
      }

      onSuccess?.();
      handleCancel();
    } catch (error) {
      handleInvoiceError(error, isEditMode);
    } finally {
      setLoading(false);
    }
  };

  const handleFormValuesChange = useCallback(
    (changedValues) => {
      // Fetch fresh exchange rate when currency changes (non-blocking)
      if ("currency" in changedValues && changedValues.currency) {
        const newCurrency = changedValues.currency;
        if (newCurrency !== "$" && newCurrency !== "USD") {
          // Fetch fresh rate for the new currency (fire and forget)
          import("../../../utils/currencyUtils")
            .then(({ preloadCurrencyRate }) => preloadCurrencyRate(newCurrency))
            .catch((err) => {
              console.warn(`Failed to preload rate for ${newCurrency}:`, err);
            });
        }
      }

      if (
        "discount_amount" in changedValues ||
        "tax_rate" in changedValues ||
        "total_amount" in changedValues
      ) {
        calculateInvoiceTotals(invoiceItems);
      }

      if ("issueDate" in changedValues && paymentTerms.length > 0) {
        const updatedTerms = [...paymentTerms];
        setPaymentTerms(updatedTerms);
      }

      if (paymentTerms.length > 0) {
        // Use calculated total instead of form total_amount
        const calculatedTotal = calculatedValues.calculated_total || 0;

        const updatedTerms = paymentTerms.map((term) => {
          return {
            ...term,
            amount: (term.percentage / 100) * calculatedTotal,
          };
        });

        setPaymentTerms(updatedTerms);
      }
    },
    [calculateInvoiceTotals, invoiceItems, paymentTerms, form, calculatedValues]
  );

  const getCurrentCurrency = () => form.getFieldValue("currency") || "$";

  // Handle split invoice
  const handleSplitInvoice = async () => {
    if (!currentBrand) {
      message.error("Please select a brand before splitting the invoice");
      return;
    }

    if (invoiceItems.length === 0) {
      message.error("Please add at least one item before splitting");
      return;
    }

    // Check if items have different LINE-ITEM payment terms
    // Items WITHOUT line-item payment terms will use global payment terms (if they exist) and stay together
    const paymentTermGroups = new Set();
    let hasItemsWithoutLineTerms = false;
    const hasGlobalPaymentTerms = paymentTerms && paymentTerms.length > 0;

    invoiceItems.forEach((item) => {
      const itemPaymentTerms = item.payment_terms || [];

      // Only consider items WITH line-item payment terms for splitting
      // Items without line-item payment terms will use global payment terms (if they exist)
      if (itemPaymentTerms.length === 0) {
        // Only count as "without line terms" if global payment terms exist
        // If no global payment terms, these items should still be split (they'll use default)
        if (hasGlobalPaymentTerms) {
          hasItemsWithoutLineTerms = true;
        }
        // Don't add to groups - these will be grouped together using global payment terms
      } else {
        // Create a unique key for each combination of payment terms
        const termsKey = itemPaymentTerms
          .map((pt) => `${pt.percentage}-${pt.due_days}`)
          .sort()
          .join("|");
        paymentTermGroups.add(termsKey);
      }
    });

    // Count total groups: items with different line-item payment terms + one group for items without line-item payment terms (if global terms exist)
    const totalGroups =
      paymentTermGroups.size + (hasItemsWithoutLineTerms ? 1 : 0);

    // If all items have line-item payment terms and they're all the same, no split needed
    // If items have different line-item payment terms, split them
    // If some items have line-item terms and some don't (and global terms exist), split them
    if (totalGroups <= 1 && paymentTermGroups.size <= 1) {
      message.warning(
        "All items have the same payment terms. No split needed."
      );
      return;
    }

    Modal.confirm({
      title: "Split Invoice",
      content: `This will split the invoice into ${totalGroups} separate invoices based on payment terms. Items with different line-item payment terms will be split into separate invoices (A, B, C, etc.). Items without line-item payment terms will use the global payment terms and stay together. The original invoice will be deleted. Continue?`,
      okText: "Yes, Split",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        try {
          let invoiceIdToSplit = invoiceId;

          // If it's a new invoice, save it first
          if (!isEditMode || !invoiceId) {
            const values = form.getFieldsValue();
            const valuesWithType = {
              ...values,
              invoiceType: defaultInvoiceType,
            };

            const invoiceData = formatInvoiceData(
              valuesWithType,
              calculatedValues,
              invoiceItems,
              currentBrand
            );
            const formData = prepareFormData(invoiceData, values);
            formData.append("payment_terms", JSON.stringify(paymentTerms));

            const response = await createInvoiceWithFile(formData, "v2");
            // Handle different response structures
            if (response && response.data) {
              invoiceIdToSplit = response.data.id || response.data;
            } else if (response && response.id) {
              invoiceIdToSplit = response.id;
            } else {
              throw new Error("Failed to get invoice ID from response");
            }
            message.success("Invoice created. Splitting now...");

            // Small delay to ensure invoice and items are fully saved to database
            // The backend transaction ensures items are saved, so we just need a brief delay
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          // Split the invoice
          const splitResponse = await splitInvoice(invoiceIdToSplit);
          const splitInvoices = splitResponse.split_invoices || [];

          Modal.success({
            title: "Invoice Split Successfully",
            width: 500,
            content: (
              <div>
                <p>Invoice split into {splitInvoices.length} invoices:</p>
                <ul style={{ marginTop: 16 }}>
                  {splitInvoices.map((inv) => (
                    <li key={inv.id} style={{ marginBottom: 8 }}>
                      <strong>{inv.number}</strong> - $
                      {parseFloat(inv.total_amount).toFixed(decimalPrecision)} (
                      {inv.payment_term})
                    </li>
                  ))}
                </ul>
              </div>
            ),
            onOk: () => {
              clearDraft();
              onSuccess?.();
              handleCancel();
            },
          });
        } catch (error) {
          console.error("Error splitting invoice:", error);
          let errorMsg = "Failed to split invoice";

          if (error?.response?.data) {
            // Handle different error response formats
            if (typeof error.response.data === "string") {
              errorMsg = error.response.data;
            } else if (error.response.data.error) {
              errorMsg = error.response.data.error;
            } else if (error.response.data.message) {
              errorMsg = error.response.data.message;
            } else if (error.response.data.detail) {
              errorMsg = error.response.data.detail;
            } else if (typeof error.response.data === "object") {
              // Try to extract error messages from nested objects
              const errorKeys = Object.keys(error.response.data);
              if (errorKeys.length > 0) {
                const firstError = error.response.data[errorKeys[0]];
                if (Array.isArray(firstError)) {
                  errorMsg = `${errorKeys[0]}: ${firstError.join(", ")}`;
                } else if (typeof firstError === "string") {
                  errorMsg = `${errorKeys[0]}: ${firstError}`;
                } else {
                  errorMsg = JSON.stringify(error.response.data);
                }
              }
            }
          } else if (error?.message) {
            errorMsg = error.message;
          }

          message.error(errorMsg);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getIssueDate = () => {
    const issueDateValue = form.getFieldValue("issueDate");
    return issueDateValue ? issueDateValue.format("YYYY-MM-DD") : null;
  };

  const columns = createInvoiceItemColumns(
    products,
    categories,
    accounts,
    offers,
    handleProductChange,
    handleItemChange,
    getCurrentCurrency,
    setInvoiceItems,
    calculateInvoiceTotals,
    removeItem,
    getCurrentCurrency(),
    mailingOffers,
    marketingFiles,
    invoiceItems,
    lists,
    segments,
    fetchSegments,
    navigate,
    paymentTerms,
    getIssueDate
  );

  // Refs to track if data needs refresh when modal opens (persists across renders)
  const categoriesNeedRefreshRef = React.useRef(false);
  const accountsNeedRefreshRef = React.useRef(false);
  const partnersNeedRefreshRef = React.useRef(false);

  // Listen for category, account, and partner updates - always listen, refresh when modal opens
  useEffect(() => {
    const handleCategoriesUpdated = () => {
      if (visible && currentBrand) {
        // Refresh immediately if modal is visible
        fetchCategories();
      } else {
        // Mark for refresh when modal opens next
        categoriesNeedRefreshRef.current = true;
      }
    };
    const handleAccountsUpdated = () => {
      // Clear the API cache to ensure fresh data
      clearAccountsCache();
      if (visible) {
        // Refresh immediately if modal is visible
        fetchAccounts();
      } else {
        // Mark for refresh when modal opens next
        accountsNeedRefreshRef.current = true;
      }
    };

    const handlePartnersUpdated = () => {
      if (visible && currentBrand) {
        // Refresh partners immediately if modal is visible
        fetchPartners();
      } else {
        // Mark for refresh when modal opens next
        partnersNeedRefreshRef.current = true;
      }
    };

    window.addEventListener("categoriesUpdated", handleCategoriesUpdated);
    window.addEventListener("accountsUpdated", handleAccountsUpdated);
    window.addEventListener("partnersUpdated", handlePartnersUpdated);

    return () => {
      window.removeEventListener("categoriesUpdated", handleCategoriesUpdated);
      window.removeEventListener("accountsUpdated", handleAccountsUpdated);
      window.removeEventListener("partnersUpdated", handlePartnersUpdated);
    };
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  // Refresh categories/accounts/partners when modal opens if they were updated while closed
  useEffect(() => {
    if (visible) {
      // Always fetch fresh data when modal opens to ensure latest categories/accounts/partners
      if (currentBrand) {
        fetchCategories();
        fetchPartners();
        // Reset the refresh flags
        categoriesNeedRefreshRef.current = false;
        partnersNeedRefreshRef.current = false;
      }
      fetchAccounts();
      accountsNeedRefreshRef.current = false;
    }
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  // Consolidated effect: fetch initial data when modal opens
  useEffect(() => {
    if (visible) {
      // Fetch brands
      fetchBrands();

      // Fetch accounts once (they're global, no brand needed)
      fetchAccounts();

      // If there's an initial currency set, fetch its rate
      const initialCurrency = form.getFieldValue("currency");
      if (
        initialCurrency &&
        initialCurrency !== "$" &&
        initialCurrency !== "USD"
      ) {
        import("../../../utils/currencyUtils").then(
          ({ preloadCurrencyRate }) => {
            preloadCurrencyRate(initialCurrency).catch(() => {
              // Failed to preload rate - will fallback to default
            });
          }
        );
      }
    }
  }, [visible, fetchBrands, fetchAccounts, form]);

  // Set currentBrand from selectedBrand prop when modal opens
  useEffect(() => {
    if (visible && selectedBrand) {
      const brandId =
        typeof selectedBrand === "object" ? selectedBrand.id : selectedBrand;
      const currentBrandId = currentBrand
        ? typeof currentBrand === "object"
          ? currentBrand.id
          : currentBrand
        : null;
      if (!currentBrandId || brandId !== currentBrandId) {
        setCurrentBrand(selectedBrand);
      }
    }
  }, [visible, selectedBrand, currentBrand]);

  // Fetch brands when selectedClients changes
  useEffect(() => {
    if (selectedClients && selectedClients.length > 0) {
      fetchBrands();
    }
  }, [selectedClients, fetchBrands]);

  // Separate useEffect for edit mode - fetch invoice data first to get the brand
  useEffect(() => {
    if (invoiceId && !isEditMode && brands.length > 0) {
      setIsEditMode(true);
      fetchInvoiceData(invoiceId);
    }
  }, [invoiceId, isEditMode, brands, fetchInvoiceData]);

  // Fetch partners when modal opens and brand is available
  useEffect(() => {
    if (visible && currentBrand) {
      fetchPartners();
    }
  }, [visible, currentBrand, fetchPartners]);

  // Reset the pre-filled flag when modal closes
  useEffect(() => {
    if (!visible) {
      initialDataPrefilledRef.current = false;
    }
  }, [visible]);

  // Pre-fill form with initial data from PaymentAllocation (partner, description, amount, date, number)
  // This should only run ONCE when the modal opens, not on every re-render
  useEffect(() => {
    // Only pre-fill if modal just opened, not in edit mode, and we haven't already pre-filled
    if (
      visible &&
      !isEditMode &&
      !invoiceId &&
      !initialDataPrefilledRef.current
    ) {
      // Pre-fill brand from selectedBrand prop
      if (selectedBrand && !form.getFieldValue("brand")) {
        const brandId = typeof selectedBrand === "object" ? selectedBrand.id : selectedBrand;
        if (brandId) {
          form.setFieldsValue({ brand: brandId });
        }
      }

      // Pre-fill issue date from transaction date if provided
      if (initialIssueDate) {
        const issueDate = dayjs(initialIssueDate);
        if (issueDate.isValid()) {
          form.setFieldsValue({ issueDate: issueDate });
        }
      }

      // Pre-fill invoice number from transaction ID if provided
      if (initialInvoiceNumber) {
        form.setFieldsValue({ number: initialInvoiceNumber });
      }

      // Pre-fill invoice item with description and amount if provided and no items exist yet
      if ((initialDescription || initialAmount) && invoiceItems.length === 0) {
        const newItem = {
          key: Date.now(),
          productId: null,
          categoryId: null,
          accountId: null,
          productName: "",
          productVariation: "",
          description: initialDescription || "",
          quantity: 1,
          unitPrice: initialAmount ? Math.abs(initialAmount) : 0,
          taxRate: 0,
          taxAmount: 0,
          discountAmount: 0,
          subtotal: initialAmount ? Math.abs(initialAmount) : 0,
          total: initialAmount ? Math.abs(initialAmount) : 0,
          offerId: null,
          offerIds: [],
          productType: "general",
          printType: null,
          envelope: null,
          marketingOffer: null,
          listId: null,
          segmentId: null,
          payment_terms: [],
        };
        setInvoiceItems([newItem]);

        // Calculate totals for the new item
        setTimeout(() => {
          calculateInvoiceTotals([newItem]);
        }, 100);
      }

      // Mark that we've pre-filled the initial data so we don't overwrite user edits
      // Only mark as pre-filled if we had any initial data to pre-fill
      if (
        initialPartnerId ||
        initialIssueDate ||
        initialInvoiceNumber ||
        initialDescription ||
        initialAmount
      ) {
        initialDataPrefilledRef.current = true;
      }
    }
  }, [
    visible,
    isEditMode,
    invoiceId,
    initialPartnerId,
    initialDescription,
    initialAmount,
    initialIssueDate,
    initialInvoiceNumber,
    selectedBrand,
    invoiceItems.length,
    form,
    calculateInvoiceTotals,
  ]);

  // Separate effect for partner pre-fill: waits until partners are actually loaded
  const partnerPrefilledRef = React.useRef(false);
  useEffect(() => {
    if (!visible) {
      partnerPrefilledRef.current = false;
      return;
    }
    if (
      visible &&
      !isEditMode &&
      !partnerPrefilledRef.current &&
      initialPartnerId &&
      partners.length > 0
    ) {
      const partnerExists = partners.find((p) => p.id === initialPartnerId);
      if (partnerExists) {
        form.setFieldsValue({ partner: initialPartnerId });
        setSelectedPartner(partnerExists);
        partnerPrefilledRef.current = true;
      }
    }
  }, [visible, isEditMode, initialPartnerId, partners, form]);

  useEffect(() => {
    if (currentBrand) {
      // Set the invoice type in the form
      form.setFieldsValue({
        invoiceType: defaultInvoiceType,
      });

      // Fetch brand-dependent data (accounts are already fetched globally)
      fetchPartners();
      fetchCategories();
      fetchOffers();
      fetchMailingOffers();
      fetchMarketingFiles();
      fetchLists();
    } else {
      // Only clear data if modal is not visible or we're not in edit mode waiting for brand
      if (!visible || !invoiceId) {
        setPartners([]);
        setProducts([]);
        setCategories([]);
        // Don't clear accounts - they're global and already fetched
        setOffers([]);
        setMailingOffers([]);
        setMarketingFiles([]);
        setLists([]);
        setSegments([]);
      }
    }
  }, [
    currentBrand,
    visible,
    invoiceId,
    fetchPartners,
    fetchCategories,
    fetchOffers,
    fetchMailingOffers,
    fetchMarketingFiles,
    fetchLists,
    form,
    defaultInvoiceType,
  ]);

  const handleCancel = () => {
    form.resetFields();
    setInvoiceItems([]);
    setPaymentTerms([
      {
        id: Date.now(),
        code: "",
        description: "",
        percentage: 100,
        due_days: 30,
        amount: 0,
        active: true,
      },
    ]);
    setCalculatedValues({
      calculated_subtotal: 0,
      calculated_taxAmount: 0,
      calculated_discountAmount: 0,
      calculated_total: 0,
      match: true,
    });
    setIsEditMode(false);
    clearDraft();
    onCancel();
  };

  return (
    <Modal
      title={
        isEditMode
          ? `Edit ${defaultInvoiceType === "bill" ? "Bill" : "Invoice"}`
          : `Create New ${defaultInvoiceType === "bill" ? "Bill" : "Invoice"}`
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ top: 20 }}
      zIndex={2000}
      destroyOnClose
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        <InvoiceFormLayout
          form={form}
          isEditMode={isEditMode}
          loading={loading}
          invoiceItems={invoiceItems}
          setInvoiceItems={setInvoiceItems}
          partners={partners}
          paymentTerms={paymentTerms}
          setPaymentTerms={setPaymentTerms}
          categories={categories}
          products={products}
          accounts={accounts}
          offers={offers}
          columns={columns}
          calculatedValues={calculatedValues}
          setCalculatedValues={setCalculatedValues}
          onFinish={onFinish}
          handlePartnerChange={handlePartnerChange}
          handleProductChange={handleProductChange}
          handleItemChange={handleItemChange}
          calculateInvoiceTotals={calculateInvoiceTotals}
          addItem={addItem}
          removeItem={removeItem}
          addPaymentTerm={addPaymentTerm}
          removePaymentTerm={removePaymentTerm}
          updatePaymentTerm={updatePaymentTerm}
          navigate={navigate}
          selectedBrand={currentBrand}
          onValuesChange={handleFormValuesChange}
          marketingFiles={marketingFiles}
          brands={filteredBrands}
          allBrands={brands}
          onBrandChange={handleBrandChange}
          defaultInvoiceType={defaultInvoiceType}
          fileList={[]}
          onSplitInvoice={handleSplitInvoice}
        />
      </div>
    </Modal>
  );
};

export default CreateInvoiceModal;
